/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Acquisition, ProductPlanTier, UserService } from './user.service';
import { XdaStringsService } from './xda-strings.service';
import { ErrorService } from './error.service';

@Injectable({
  providedIn: 'root',
})
export class AppsService {
  baseUrl = 'api/apps/';

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private xdaStringsService: XdaStringsService,
    private errorService: ErrorService,
  ) {}

  async getAcquisition(userId: string, appId: string) {
    return await this.http
      .get<Acquisition>(`${this.baseUrl}license-info`, {
        params: { userId: userId, appId: appId },
      })
      .toPromise();
  }

  async getTOU(userId: string) {
    return await this.http
      .get<string>(`${this.baseUrl}app-tou`, {
        params: { userId: userId || '' },
      })
      .toPromise();
  }

  async getPrivacyPolicy() {
    return await this.http.get(`${this.baseUrl}privacy-policy`).toPromise();
  }

  async updateAppId(appId: string) {
    return await this.http
      .get(`${this.baseUrl}update-app-id`, { params: { appId: appId } })
      .toPromise();
  }

  async getActiveAcquisitions(userId: string) {
    const appId = this.xdaStringsService.strings['APPID'];
    try {
      // await the value before updating acquisition.
      let latestAcquisition: Acquisition | null = await this.getAcquisition(
        userId,
        appId,
      );

      // If we got an acquisition with a special status code error
      // Set the error, and then treat out of usage as any other (null) expired acquisition
      if (latestAcquisition?.specialStatusCode) {
        this.errorService.specialStatusCode =
          latestAcquisition.specialStatusCode;
        latestAcquisition = null;
      }

      if (latestAcquisition?.productPlanTier == ProductPlanTier.None) {
        this.errorService.displayGeneralError().result.then(() => {
          this.userService.logOff();
        });
      } else {
        if (this.userService.user) {
          this.userService.user.acquisition = latestAcquisition;
          this.userService.user.isSubscriptionExpired = !latestAcquisition;
        }        
      }
    } catch {
      if (this.userService.user) {
        this.userService.user.acquisition = null;
      }
    }
    this.userService.updateUser();
  }
}
